import { useRef } from 'react';
import type React from 'react';

/**
 * [FLUID-8 2026-10-12, user] "just like you added this thing to dropdown, the fastness I want the same to the complete
 * application". A click only fires when the mouse button comes back UP — about 0.1 s into a normal click — so everything
 * that opened on click waited for the release. Controls that OPEN or SWITCH something (menus, calendars, popovers, side
 * panels, the dock's tabs, view pills, filter chips) act on the mouse PRESS instead, as the browser's own menus, selects
 * and tabs do. Touch and pen keep the click (a press there may be the start of a scroll); the keyboard keeps Enter / Space
 * (they click). Buttons that DO something (save, delete, add) or open a window keep the click: a press there can still be
 * cancelled by sliding off before letting go.
 *
 * Usage: `const press = usePressAction(() => setOpen((o) => !o));` then `<button {...press}>`.
 * The click that follows a handled press is ignored, so a toggle never opens and closes in one click.
 */
export function usePressAction<E extends Element = Element>(action: () => void, enabled = true) {
  const pressed = useRef(false);
  return {
    onPointerDown: (e: React.PointerEvent<E>) => {
      pressed.current = enabled && isPlainMousePress(e);
      if (pressed.current) action();
    },
    onClick: (e: React.MouseEvent<E>) => {
      if (clickEndsPress(pressed, e)) return; // the press already did it
      if (enabled) action();
    },
  };
}

/** The press that acts at once: the LEFT mouse button, no modifier key (on a Mac Ctrl + press is the right-click menu;
 *  a modified press keeps the click), on an enabled control (Chrome sends pointer events to disabled buttons too). */
export function isPlainMousePress(e: React.PointerEvent): boolean {
  const el = e.currentTarget as Element & { disabled?: boolean };
  return e.pointerType === 'mouse' && e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey && !el.disabled;
}

/** Whether this click is the release of a press that already acted (and clears the mark). A keyboard click (Enter /
 *  Space: `detail` 0) never is — so a press whose release landed elsewhere (a backdrop that opened under the pointer,
 *  a slide off the button) can never swallow a later keypress. */
export function clickEndsPress(pressed: { current: boolean }, e: React.MouseEvent): boolean {
  const ends = pressed.current && e.detail !== 0;
  pressed.current = false;
  return ends;
}

export default usePressAction;
